import { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'

import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { useRealtimeAttendanceContext } from '@/modules/RealtimeAttendance/contexts/RealtimeAttendanceContext'
import { sendMetrics } from '@/modules/RealtimeAttendance/realtime-attendance.services'
import { useStyles } from './useStyles'
import { ATTENDANCE_STATES } from '@/modules/RealtimeAttendance/realtime-attendance-constants'
import CustomLayout from '@/components/CustomLayout'
import { Button } from '@conexasaude/hero-web'
import InviteToCallModal from '../../components/InviteToCallModal'
import { RenderIf } from '@/components/RenderIf'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'

enum MEETING_TYPE {
  ANYMEET = 'ANYMEET',
  VONAGE = 'VONAGE',
}

const VideoRoom: FunctionComponent = () => {
  const history = useHistory()
  const [sessionId, setSessionId] = useState('')

  const { realtimeAttendance, callingTimes, clearTimer } =
    useRealtimeAttendanceContext()
  const { clinic } = useAuthContext()
  const { showModal } = useModalContext()

  const { container, meetingHeader, chatWrapper } = useStyles()

  const unblock = history.block(() => {
    if (realtimeAttendance?.call?.queueStatus) {
      switch (realtimeAttendance?.call?.queueStatus) {
        case ATTENDANCE_STATES.CALL_STARTED:
          return false
        case ATTENDANCE_STATES.CALL_RESTARTED:
          return false
      }
    }
  })

  useEffect(() => {
    if (!realtimeAttendance?.call) return

    const sendMetricsAndClearTimer = async () => {
      await sendMetrics(realtimeAttendance?.call?.callId, {
        callingTimes,
        status: realtimeAttendance?.call?.queueStatus,
      })

      clearTimer()
    }
    sendMetricsAndClearTimer()

    const urlParams = new URLSearchParams(
      realtimeAttendance?.call?.zoomMeetingUrl,
    )
    setSessionId(urlParams.get('sessionId'))
    // eslint-disable-next-line
  }, [realtimeAttendance?.call])

  useEffect(() => {
    return () => {
      unblock()
    }
  }, [unblock])

  const handleInviteClick = () => {
    showModal('INVITE_TO_CALL_MODAL', {
      title: 'Convide alguém para a chamada',
    })
  }

  const isAnymeet = useMemo(() => {
    return realtimeAttendance?.call?.meetingType === MEETING_TYPE.ANYMEET
  }, [realtimeAttendance?.call?.meetingType])

  const canInvitePeople = useMemo(() => {
    return (
      clinic?.flag?.PermiteCriacaoUrlConvidado &&
      realtimeAttendance?.call?.meetingType &&
      !isAnymeet
    )
  }, [
    clinic?.flag?.PermiteCriacaoUrlConvidado,
    isAnymeet,
    realtimeAttendance?.call?.meetingType,
  ])

  const meetingUrl = useMemo(() => {
    let params = ''

    if (isAnymeet) {
      params += '?skipGuide=true'
    }

    return `${realtimeAttendance?.call?.zoomMeetingUrl}${params}`
  }, [isAnymeet, realtimeAttendance?.call?.zoomMeetingUrl])

  return (
    <CustomLayout>
      <div css={container}>
        <div className="row">
          <div className="col-12" css={meetingHeader}>
            <h3>
              Videochamada com {realtimeAttendance?.call?.professionalName}
            </h3>

            <ul>
              <li>
                Tipo de consulta:
                <span>
                  {realtimeAttendance?.isImmediate ? 'Imediata' : 'Agendada'}
                </span>
              </li>
              <li>
                Especialidade:{' '}
                <span>{realtimeAttendance?.call?.speciality}</span>
              </li>
            </ul>
            <RenderIf condition={canInvitePeople}>
              <Button onClick={() => handleInviteClick()} size="sm">
                Convidar pessoas
              </Button>
            </RenderIf>
          </div>
        </div>
        <div className="row">
          <div className={`col-12 ${clinic?.canChat ? 'col-lg-8' : ''}`}>
            <iframe
              src={meetingUrl}
              width="100%"
              height="550"
              name="meetConference"
              allow="camera; microphone; display-capture;"
              allowFullScreen
            />
          </div>

          {clinic?.canChat && (
            <div className="col-12 col-lg-4" css={chatWrapper}></div>
          )}
        </div>
      </div>
      <RenderIf condition={clinic?.flag?.PermiteCriacaoUrlConvidado}>
        <InviteToCallModal sessionId={sessionId} />
      </RenderIf>
    </CustomLayout>
  )
}

export default VideoRoom
